/**
 * @component ry-questionnaire
 * @category  form
 * @summary   Multi-step question flow (ports shadcn/ui's Questionnaire). Direct
 *            `<ry-question>` children are the steps; one is active at a time.
 *            Handles navigation, per-question `required` gating, conditional
 *            questions via `[when]`, an optional `<ry-step>` progress rail, focus
 *            movement on navigate, and answer collection. Choices render as
 *            `ry-tile-radio` / `ry-tile-checkbox`, so a question is a real radio
 *            group and a wrapping `<ry-form>` sees it as a normal control.
 *
 * @attribute persist       string?   localStorage key. Answers + the active question are saved under `ry-questionnaire:<key>` on every change and restored at mount. Restore is keyed by question NAME, so reordering questions doesn't corrupt a saved run.
 * @attribute progress      boolean   default=false   Render a `<ry-step>` rail above the questions, one step per ELIGIBLE question. Steps mark `[completed]` / `[active]`; `ry-step` supplies the ARIA.
 * @attribute skippable     boolean   default=false   Show a Skip action on questions that are not `[required]`. Skipping advances without recording an answer.
 * @attribute next-label    string?   default="Next"       Label for the advance action.
 * @attribute prev-label    string?   default="Back"       Label for the reverse action.
 * @attribute skip-label    string?   default="Skip"       Label for the skip action.
 * @attribute submit-label  string?   default="Submit"     Label shown instead of Next on the last eligible question.
 *
 * @property  answers        Record<string, string | string[]>   Live answers keyed by question name. Single-choice questions yield a string; `[multiple]` questions yield an array. Unanswered and ineligible questions are omitted entirely.
 * @property  questions      RyQuestion[]   All `<ry-question>` children in DOM order, including ineligible ones.
 * @property  eligible       RyQuestion[]   Only the questions whose `[when]` condition currently passes — the list the user actually walks.
 * @property  activeIndex    number         Index into `questions` (ALL of them). Setting it activates that question directly, with no validation. See the gotcha.
 * @property  activeQuestion RyQuestion | null   The currently active question element.
 * @property  complete       boolean        True once `submit()` has succeeded.
 *
 * @method    next()      boolean   Validate the active question, then advance to the next eligible one. Returns false (and emits `ry:blocked`) when a `[required]` question is unanswered, or when `ry:beforenavigate` was vetoed. On the last eligible question this delegates to `submit()`.
 * @method    prev()      boolean   Move back to the previous eligible question. Never validates — going back is always allowed. Returns false at the first question or when `ry:beforenavigate` was vetoed.
 * @method    skip()      boolean   Advance without validating. Returns false if the active question is `[required]` or when `ry:beforenavigate` was vetoed.
 * @method    submit()    boolean   Validate the active question, then emit `ry:complete` and enter the complete state. Returns false when validation blocks it.
 * @method    reset()     void      Clear every answer, drop persisted state, and return to the first question.
 *
 * @event ry:answer
 *        detail: { name: string, value: string | string[] | undefined, answers: Record<string, string | string[]> }
 *        fires:  whenever an answer changes — a choice is picked/unpicked, or a freeform input is edited. Not fired by navigation.
 *
 * @event ry:beforenavigate
 *        detail: { from: number, to: number, name: string }
 *        fires:  before the active question changes — next / prev / skip / Enter / the action buttons / the `activeIndex` setter — after validation passed and before `activeIndex`, the DOM, or the `[persist]` record change. `name` is the question about to become active. Indices are into `questions`.
 *        cancelable: preventDefault() stays on the current question; `ry:navigate` does not fire and next()/prev()/skip() return false
 *
 * @event ry:navigate
 *        detail: { from: number, to: number, name: string }
 *        fires:  after the active question changes (next / prev / skip / activeIndex setter). Indices are into `questions`.
 *
 * @event ry:blocked
 *        detail: { name: string, message: string }
 *        fires:  when an advance is blocked because a `[required]` question is unanswered.
 *
 * @event ry:complete
 *        detail: { answers: Record<string, string | string[]> }
 *        fires:  on a successful `submit()`, before the complete state is entered.
 *        cancelable: preventDefault() keeps the questionnaire in its active state instead of completing; submit() returns false — the cancelable-under-its-plain-name exception, see ARCHITECTURE.md
 *
 * @gotcha  There is no `ry:beforeanswer`. `ry:answer` is a notification of a
 *          change the native input already made (and fires per keystroke for a
 *          freeform control). To veto a choice, listen for the tile's own
 *          `ry:beforechange` — it bubbles through `<ry-questionnaire>`, and
 *          preventDefault() cancels the click before the input toggles, so the
 *          answer never lands and `ry:answer` never fires.
 *
 * @gotcha  The events are `ry:answer` / `ry:blocked` / `ry:complete`, NOT
 *          `ry:change` / `ry:invalid` / `ry:submit`. All three of those bubble
 *          through this component from something else — `ry:change` from the
 *          `ry-tile-radio` choices, `ry:invalid` and `ry:submit` from a wrapping
 *          `<ry-form>` — so reusing them would make a listener on the host
 *          unable to tell whose event it caught. Listen for `ry:change` if you
 *          want per-choice granularity; listen for `ry:answer` for the
 *          question-level answer.
 *
 * @keyboard  Enter — advances (next / submit) unless focus is in a `<textarea>`.
 *            Propagation is stopped so a wrapping `<ry-form>` doesn't also submit.
 *            Choice navigation (arrows) is native radio/checkbox behavior.
 *
 * @state     `data-ry-state` on the host: 'active' while the user is answering,
 *            'complete' after a successful submit. Each `<ry-question>` carries
 *            `data-ry-state="active" | "pending" | "done"`, and ineligible
 *            questions get `hidden` plus `data-ry-eligible="false"`.
 *
 * @content   Direct `<ry-question>` children, in the order they should be asked.
 *            Anything else at the top level is left alone (the progress rail and
 *            the actions bar are inserted around them).
 *
 * @dom
 *   ry-questionnaire[persist?][progress?][skippable?]   data-ry-state="active|complete"
 *     > ry-step[data-ry-target="progress"]              (only when [progress])
 *       > span[data-ry-target="step"][active?][completed?]   — one per ELIGIBLE question
 *     > ry-question[data-ry-target="question"]  data-ry-state="active|pending|done"
 *       > div[data-ry-target="prompt"]      tabindex="-1"    (focus lands here on navigate)
 *       > div[data-ry-target="description"]                  (only when [description])
 *       > div[data-ry-target="choices"]     role="group"
 *         > ry-tile-radio | ry-tile-checkbox                 — one per <ry-choice>
 *       > {any freeform <input>/<textarea> authored inside, left in place}
 *       > div[data-ry-target="error"]  role="alert"
 *     > div[data-ry-target="actions"]
 *       > ry-button[data-ry-target="prev"]
 *       > ry-button[data-ry-target="skip"]     (only when [skippable] and question is optional)
 *       > ry-button[data-ry-target="next"]     (label swaps to submit-label on the last question)
 *
 * @gotcha  `activeIndex` indexes ALL questions, not the visible progress
 *          position. A questionnaire with a hidden conditional question at
 *          index 1 goes 0 → 2 on `next()`, and the progress rail still reads
 *          "2 of 3". Use `activeQuestion.name` when you need a stable identifier.
 *
 * @gotcha  `[when]` SKIPS a question rather than disabling it — an ineligible
 *          question is hidden, excluded from navigation and progress, and its
 *          answer is dropped from `answers` even if it was answered before the
 *          condition flipped. Answers are never silently carried by a branch
 *          the user didn't end up taking.
 *
 * @gotcha  `<ry-choice>` children are read ONCE at setup and replaced by
 *          rendered tiles. Setting `.innerHTML` on an already-upgraded
 *          `<ry-question>` does NOT re-render — replace the PARENT's innerHTML
 *          with a fresh `<ry-questionnaire>` block instead. Same constraint as
 *          `ry-select` / `ry-filter`.
 *
 * @gotcha  A question answered by BOTH a choice and a freeform input counts as
 *          answered by either. For single-choice the picked choice wins over the
 *          input; for `[multiple]` a non-empty input value is appended to the array.
 *
 * @example basic
 * ```html
 * <ry-questionnaire>
 *   <ry-question name="role" prompt="What's your role?" required>
 *     <ry-choice value="eng">Engineer</ry-choice>
 *     <ry-choice value="design">Designer</ry-choice>
 *   </ry-question>
 *   <ry-question name="size" prompt="How big is your team?">
 *     <ry-choice value="solo">Just me</ry-choice>
 *     <ry-choice value="small">2–10</ry-choice>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @example with-progress
 * ```html
 * <ry-questionnaire progress skippable>
 *   <ry-question name="a" prompt="First" required>
 *     <ry-choice value="1">One</ry-choice>
 *     <ry-choice value="2">Two</ry-choice>
 *   </ry-question>
 *   <ry-question name="b" prompt="Second">
 *     <ry-choice value="x">X</ry-choice>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @example multiple-choice
 * ```html
 * <ry-questionnaire>
 *   <ry-question name="stack" prompt="Which stacks?" description="Pick all that apply." multiple>
 *     <ry-choice value="ts" icon="code">TypeScript</ry-choice>
 *     <ry-choice value="rust" icon="code">Rust</ry-choice>
 *     <ry-choice value="go" icon="code">Go</ry-choice>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @example conditional-branch
 * ```html
 * <ry-questionnaire>
 *   <ry-question name="role" prompt="Role?" required>
 *     <ry-choice value="eng">Engineer</ry-choice>
 *     <ry-choice value="other">Something else</ry-choice>
 *   </ry-question>
 *   <ry-question name="lang" prompt="Favourite language?" when="role=eng">
 *     <ry-choice value="ts">TypeScript</ry-choice>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @example freeform
 * ```html
 * <ry-questionnaire>
 *   <ry-question name="why" prompt="Why are you here?" required>
 *     <textarea rows="3" placeholder="A sentence is plenty"></textarea>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @example persisted
 * ```html
 * <ry-questionnaire persist="onboarding" progress>
 *   <ry-question name="role" prompt="Role?" required>
 *     <ry-choice value="eng">Engineer</ry-choice>
 *   </ry-question>
 * </ry-questionnaire>
 * ```
 *
 * @tutorial basic-interaction
 * ```js
 * // Pick a choice, the answer lands in .answers keyed by question name.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="role" prompt="Role?">
 *       <ry-choice value="eng">Engineer</ry-choice>
 *       <ry-choice value="design">Designer</ry-choice>
 *     </ry-question>
 *   </ry-questionnaire>
 * `);
 * const [eng] = el.querySelectorAll('ry-tile-radio');
 * eng.querySelector('input').click();
 * await expectEqual(el.answers.role, 'eng', 'answer recorded under the question name');
 * ```
 *
 * @tutorial keyboard-nav
 * ```js
 * // Enter advances, exactly like clicking Next.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="a" prompt="A"><ry-choice value="1">One</ry-choice></ry-question>
 *     <ry-question name="b" prompt="B"><ry-choice value="2">Two</ry-choice></ry-question>
 *   </ry-questionnaire>
 * `);
 * await expectEqual(el.activeIndex, 0);
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.activeIndex, 1, 'Enter advanced to the second question');
 * ```
 *
 * @tutorial programmatic-api
 * ```js
 * // next() returns false and emits ry:blocked when a required question is blank.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="a" prompt="A" required><ry-choice value="1">One</ry-choice></ry-question>
 *     <ry-question name="b" prompt="B"><ry-choice value="2">Two</ry-choice></ry-question>
 *   </ry-questionnaire>
 * `);
 * await expectEqual(el.next(), false, 'blocked — required and unanswered');
 * await expectEqual(el.activeIndex, 0, 'still on the first question');
 * el.querySelector('ry-tile-radio input').click();
 * await expectEqual(el.next(), true, 'advances once answered');
 * await expectEqual(el.activeIndex, 1);
 * await expectEqual(el.prev(), true, 'prev never validates');
 * await expectEqual(el.activeIndex, 0);
 * ```
 *
 * @tutorial beforenavigate-veto
 * ```js
 * // preventDefault() on ry:beforenavigate keeps the current question active —
 * // whatever asked to move (next(), prev(), Enter, the buttons, activeIndex=).
 * // ry:navigate stays silent and next() reports false.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="a" prompt="A"><ry-choice value="1">One</ry-choice></ry-question>
 *     <ry-question name="b" prompt="B"><ry-choice value="2">Two</ry-choice></ry-question>
 *   </ry-questionnaire>
 * `);
 * const navs = [];
 * el.addEventListener('ry:navigate', (e) => navs.push(e.detail));
 * let seen = null;
 * el.addEventListener('ry:beforenavigate', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * await expectEqual(el.next(), false, 'vetoed next() returns false');
 * await expectEqual(el.activeIndex, 0, 'still on the first question');
 * await expectEqual(seen.to, 1, 'detail names the target question index');
 * await expectEqual(seen.name, 'b');
 * await expectEqual(navs.length, 0, 'ry:navigate did not fire');
 * await expectEqual(el.next(), true, 'without the veto, next() advances');
 * await expectEqual(el.activeIndex, 1);
 * await expectEqual(navs.length, 1);
 * ```
 *
 * @tutorial conditional-when-skips
 * ```js
 * // [when] removes a question from the flow entirely — next() jumps over it.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="role" prompt="Role?">
 *       <ry-choice value="eng">Engineer</ry-choice>
 *       <ry-choice value="other">Other</ry-choice>
 *     </ry-question>
 *     <ry-question name="lang" prompt="Language?" when="role=eng">
 *       <ry-choice value="ts">TypeScript</ry-choice>
 *     </ry-question>
 *     <ry-question name="done" prompt="Anything else?">
 *       <ry-choice value="no">No</ry-choice>
 *     </ry-question>
 *   </ry-questionnaire>
 * `);
 * el.querySelectorAll('ry-tile-radio')[1].querySelector('input').click(); // role=other
 * await expectEqual(el.eligible.length, 2, 'the lang question dropped out of the flow');
 * el.next();
 * await expectEqual(el.activeQuestion.name, 'done', 'jumped straight past lang');
 * ```
 *
 * @tutorial event-names-are-not-ry-change
 * ```js
 * // The choices are real ry-tile-radio elements, so THEIR ry:change bubbles
 * // through the host as well. That's why the question-level event is called
 * // ry:answer — listening for ry:change on the questionnaire gets you the
 * // per-choice event, not the answer.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="a" prompt="A"><ry-choice value="1">One</ry-choice></ry-question>
 *   </ry-questionnaire>
 * `);
 * const changes = [], answers = [];
 * el.addEventListener('ry:change', (e) => changes.push(e.detail));
 * el.addEventListener('ry:answer', (e) => answers.push(e.detail));
 * el.querySelector('ry-tile-radio input').click();
 * await expectEqual(changes.length, 1, 'the tile fired ry:change');
 * await expectEqual(changes[0].checked, true, 'tile detail is control-shaped');
 * await expectEqual(answers.length, 1, 'the questionnaire fired ry:answer');
 * await expectEqual(answers[0].name, 'a', 'answer detail is question-shaped');
 * ```
 *
 * @tutorial wrong-assumption
 * ```js
 * // A model trained on shadcn/Radix assumes activeIndex is the progress
 * // position ("step 2 of 3"). It is NOT — it indexes ALL questions, including
 * // ones [when] has hidden. Here the flow is 2 questions long but the second
 * // one sits at index 2.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="role" prompt="Role?">
 *       <ry-choice value="eng">Engineer</ry-choice>
 *       <ry-choice value="other">Other</ry-choice>
 *     </ry-question>
 *     <ry-question name="lang" prompt="Language?" when="role=eng">
 *       <ry-choice value="ts">TypeScript</ry-choice>
 *     </ry-question>
 *     <ry-question name="done" prompt="Anything else?">
 *       <ry-choice value="no">No</ry-choice>
 *     </ry-question>
 *   </ry-questionnaire>
 * `);
 * el.querySelectorAll('ry-tile-radio')[1].querySelector('input').click(); // role=other
 * el.next();
 * await expectEqual(el.eligible.length, 2, 'the user walks 2 questions');
 * await expectEqual(el.activeIndex, 2, 'but activeIndex is 2, not 1');
 * await expectEqual(el.activeQuestion.name, 'done', 'name is the stable identifier');
 * ```
 *
 * @tutorial answers-drop-ineligible
 * ```js
 * // Second wrong assumption: that an answer sticks around once given. If a
 * // later edit makes its question ineligible, the answer leaves .answers —
 * // a branch the user backed out of never contributes to the payload.
 * const el = mount(`
 *   <ry-questionnaire>
 *     <ry-question name="role" prompt="Role?">
 *       <ry-choice value="eng">Engineer</ry-choice>
 *       <ry-choice value="other">Other</ry-choice>
 *     </ry-question>
 *     <ry-question name="lang" prompt="Language?" when="role=eng">
 *       <ry-choice value="ts">TypeScript</ry-choice>
 *     </ry-question>
 *   </ry-questionnaire>
 * `);
 * const [eng, other] = el.querySelectorAll('ry-tile-radio');
 * eng.querySelector('input').click();
 * el.querySelector('ry-question[name="lang"] input').click();
 * await expectEqual(el.answers.lang, 'ts', 'answered while the branch was live');
 * other.querySelector('input').click();
 * await expectEqual(el.answers.lang, undefined, 'branch abandoned — answer dropped');
 * ```
 *
 * @chain ry-form
 * ```js
 * // Choices render as real radio inputs, so a wrapping <ry-form> collects them
 * // through its normal control sweep — no bridging code.
 * const form = mount(`
 *   <ry-form>
 *     <ry-questionnaire>
 *       <ry-question name="role" prompt="Role?">
 *         <ry-choice value="eng">Engineer</ry-choice>
 *         <ry-choice value="design">Designer</ry-choice>
 *       </ry-question>
 *     </ry-questionnaire>
 *   </ry-form>
 * `);
 * form.querySelector('ry-tile-radio input').click();
 * await expectEqual(form.values.role, 'eng', 'ry-form sees the questionnaire answer');
 * ```
 *
 * @chain ry-step
 * ```js
 * // [progress] builds a <ry-step> rail; ry-step supplies aria-current itself.
 * const el = mount(`
 *   <ry-questionnaire progress>
 *     <ry-question name="a" prompt="A"><ry-choice value="1">One</ry-choice></ry-question>
 *     <ry-question name="b" prompt="B"><ry-choice value="2">Two</ry-choice></ry-question>
 *   </ry-questionnaire>
 * `);
 * const rail = el.querySelector('[data-ry-target="progress"]');
 * await expectEqual(rail.children.length, 2, 'one step per eligible question');
 * await expectAttr(rail.children[0], 'aria-current', 'step');
 * ```
 *
 * @see ry-form
 * @see ry-step
 * @see ry-tile-radio
 * @see ry-tile-checkbox
 */

import { RyElement } from '../core/ry-element.js';

const PERSIST_PREFIX = 'ry-questionnaire:';

type Answer = string | string[];

interface PersistedRun {
  answers: Record<string, Answer>;
  activeName: string;
}

/**
 * @component ry-choice
 * @category  form
 * @summary   Declarative choice marker inside a `<ry-question>`. Read once by the
 *            parent question at setup and replaced by a `ry-tile-radio` (or
 *            `ry-tile-checkbox` when the question is `[multiple]`). Never renders
 *            on its own.
 *
 * @attribute value  string   The value recorded in `answers` when this choice is picked.
 * @attribute icon   string?  Icon name passed through to the rendered tile.
 * @attribute meta   string?  Secondary line passed through to the rendered tile.
 * @attribute badge  string?  Corner badge passed through to the rendered tile.
 *
 * @content   The choice's visible label (plain text).
 */
export class RyChoice extends RyElement {
  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  // No setup() — the parent <ry-question> consumes this element.
}

/**
 * @component ry-question
 * @category  form
 * @summary   One question inside a `<ry-questionnaire>`. Renders its prompt,
 *            optional description, its `<ry-choice>` children as tiles, and an
 *            error slot. Any native `<input>` / `<textarea>` authored inside is
 *            left in place and treated as a freeform answer.
 *
 * @attribute name         string    Answer key. Required — a question without a name is skipped entirely.
 * @attribute prompt       string?   The question text.
 * @attribute description  string?   Supporting copy shown under the prompt.
 * @attribute required     boolean   default=false   Blocks `next()` until answered.
 * @attribute multiple     boolean   default=false   Render choices as checkboxes; the answer becomes an array.
 * @attribute when         string?   Conditional. `when="role=eng"` shows this question only when the `role` answer is `eng`. `when="role=eng\|design"` matches any listed value; `when="role!=eng"` negates. Referencing an unanswered question means the condition fails.
 *
 * @property  name      string                       Mirrors the `name` attribute.
 * @property  value     string | string[] | undefined   The current answer: a string for single-choice, an array for `[multiple]`, `undefined` when unanswered.
 * @property  answered  boolean                      True when the question has any answer.
 *
 * @state     `data-ry-state`: 'active' (being asked), 'pending' (not yet reached),
 *            'done' (already answered/passed). Ineligible questions additionally
 *            carry `hidden` and `data-ry-eligible="false"`.
 *
 * @content   `<ry-choice>` children and/or a native `<input>` / `<textarea>`.
 *
 * @gotcha  `value` is a getter over live DOM state, not stored state — it reads
 *          the rendered tiles each time. There is no setter; select a choice by
 *          clicking its input, or restore a whole run via the questionnaire's
 *          `[persist]`.
 *
 * @see ry-questionnaire
 * @see ry-choice
 */
export class RyQuestion extends RyElement {
  #built = false;

  setup(): void {
    if (this.#built) return;
    this.#built = true;
    this.setAttribute('data-ry-target', 'question');
    if (!this.hasAttribute('data-ry-state')) this.setAttribute('data-ry-state', 'pending');
    this.#render();
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  /** True when [multiple] — choices are checkboxes and the answer is an array. */
  get multiple(): boolean {
    return this.hasAttribute('multiple');
  }

  /** Tiles rendered from <ry-choice> children. */
  get tiles(): HTMLElement[] {
    return this.$$<HTMLElement>('ry-tile-radio, ry-tile-checkbox');
  }

  /** The freeform control, if the author put one inside. Tile inputs excluded. */
  get freeform(): HTMLInputElement | HTMLTextAreaElement | null {
    const el = this.$<HTMLElement>('input:not(.ry-tile__input), textarea');
    return (el as HTMLInputElement | HTMLTextAreaElement | null) ?? null;
  }

  get value(): Answer | undefined {
    const picked: string[] = [];
    for (const tile of this.tiles) {
      const input = tile.querySelector<HTMLInputElement>('input.ry-tile__input');
      if (input?.checked) picked.push(input.value);
    }
    const free = this.freeform?.value.trim() ?? '';

    if (this.multiple) {
      const out = free ? [...picked, free] : picked;
      return out.length > 0 ? out : undefined;
    }
    if (picked.length > 0) return picked[0];
    return free !== '' ? free : undefined;
  }

  get answered(): boolean {
    const v = this.value;
    if (v === undefined) return false;
    return Array.isArray(v) ? v.length > 0 : v !== '';
  }

  /** Show or clear the inline error + aria-invalid. */
  setError(message: string | null): void {
    const slot = this.$<HTMLElement>('[data-ry-target="error"]');
    if (slot) slot.textContent = message ?? '';
    if (message) this.setAttribute('aria-invalid', 'true');
    else this.removeAttribute('aria-invalid');
  }

  /** Move focus to this question's prompt (called by the questionnaire on navigate). */
  focusPrompt(): void {
    this.$<HTMLElement>('[data-ry-target="prompt"]')?.focus();
  }

  #render(): void {
    // Read declarative <ry-choice> children ONCE, then build managed markup.
    const choices = [...this.children].filter(
      (c): c is HTMLElement => c.tagName.toLowerCase() === 'ry-choice'
    );
    // Freeform controls authored inside are preserved verbatim.
    const carried = [...this.children].filter(
      (c) => c.tagName.toLowerCase() !== 'ry-choice'
    );
    choices.forEach((c) => c.remove());
    carried.forEach((c) => c.remove());

    const prompt = document.createElement('div');
    prompt.setAttribute('data-ry-target', 'prompt');
    prompt.className = 'ry-questionnaire__prompt';
    prompt.setAttribute('tabindex', '-1');
    prompt.textContent = this.getAttribute('prompt') ?? '';
    this.appendChild(prompt);

    const description = this.getAttribute('description');
    if (description) {
      const desc = document.createElement('div');
      desc.setAttribute('data-ry-target', 'description');
      desc.className = 'ry-questionnaire__description';
      desc.textContent = description;
      this.appendChild(desc);
    }

    if (choices.length > 0) {
      const group = document.createElement('div');
      group.setAttribute('data-ry-target', 'choices');
      group.className = 'ry-questionnaire__choices';
      group.setAttribute('role', 'group');
      if (this.getAttribute('prompt')) {
        group.setAttribute('aria-label', this.getAttribute('prompt') ?? '');
      }
      const tag = this.multiple ? 'ry-tile-checkbox' : 'ry-tile-radio';
      for (const choice of choices) {
        const tile = document.createElement(tag);
        tile.setAttribute('name', this.name);
        tile.setAttribute('value', choice.getAttribute('value') ?? '');
        tile.setAttribute('heading', (choice.textContent ?? '').trim());
        for (const pass of ['icon', 'meta', 'badge', 'pill', 'pill-text']) {
          const v = choice.getAttribute(pass);
          if (v !== null) tile.setAttribute(pass, v);
        }
        if (choice.hasAttribute('disabled')) tile.setAttribute('disabled', '');
        group.appendChild(tile);
      }
      this.appendChild(group);
    }

    // Re-attach freeform controls after the choices.
    carried.forEach((c) => this.appendChild(c));

    const error = document.createElement('div');
    error.setAttribute('data-ry-target', 'error');
    error.className = 'ry-questionnaire__error';
    error.setAttribute('role', 'alert');
    this.appendChild(error);
  }
}

export class RyQuestionnaire extends RyElement {
  static observedAttributes = ['progress', 'skippable'] as const;

  #built = false;
  #activeIndex = 0;
  #complete = false;
  /** Last answer state emitted, so one user action emits one ry:answer. */
  #lastAnswerSnapshot = '';

  setup(): void {
    if (this.#built) return;
    this.#built = true;

    this.#renderScaffold();
    this.#restore();
    this.#syncAll();

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
    // Answer changes bubble from the tiles; freeform edits from the inputs.
    this.on(this, 'ry:change', this.#handleAnswerChange);
    this.on(this, 'input', this.#handleAnswerChange);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.#built) return;
    if (name === 'progress') this.#renderProgress();
    this.#syncAll();
  }

  // ──────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────

  get questions(): RyQuestion[] {
    return [...this.children].filter(
      (c): c is RyQuestion => c instanceof RyQuestion
    );
  }

  get eligible(): RyQuestion[] {
    return this.questions.filter((q) => q.name !== '' && this.#isEligible(q));
  }

  get answers(): Record<string, Answer> {
    const out: Record<string, Answer> = {};
    for (const q of this.eligible) {
      const v = q.value;
      if (v === undefined) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      out[q.name] = v;
    }
    return out;
  }

  get activeIndex(): number {
    return this.#activeIndex;
  }

  set activeIndex(i: number) {
    const questions = this.questions;
    if (i < 0 || i >= questions.length) return;
    void this.#goto(i);
  }

  get activeQuestion(): RyQuestion | null {
    return this.questions[this.#activeIndex] ?? null;
  }

  get complete(): boolean {
    return this.#complete;
  }

  next(): boolean {
    const active = this.activeQuestion;
    if (!active) return false;
    if (!this.#validate(active)) return false;
    const target = this.#adjacentEligible(1);
    if (target === -1) return this.submit();
    return this.#goto(target);
  }

  prev(): boolean {
    const target = this.#adjacentEligible(-1);
    if (target === -1) return false;
    this.activeQuestion?.setError(null);
    return this.#goto(target);
  }

  skip(): boolean {
    const active = this.activeQuestion;
    if (!active || active.hasAttribute('required')) return false;
    active.setError(null);
    const target = this.#adjacentEligible(1);
    if (target === -1) return this.submit();
    return this.#goto(target);
  }

  submit(): boolean {
    const active = this.activeQuestion;
    if (active && !this.#validate(active)) return false;
    const proceed = this.emit('complete', { answers: this.answers });
    if (!proceed) return false;
    this.#complete = true;
    this.state = 'complete';
    this.#syncAll();
    return true;
  }

  reset(): void {
    for (const q of this.questions) {
      for (const tile of q.tiles) {
        const input = tile.querySelector<HTMLInputElement>('input.ry-tile__input');
        if (input) input.checked = false;
        tile.removeAttribute('checked');
      }
      const free = q.freeform;
      if (free) free.value = '';
      q.setError(null);
    }
    this.#complete = false;
    this.#activeIndex = this.questions.findIndex((q) => this.#isEligible(q));
    if (this.#activeIndex < 0) this.#activeIndex = 0;
    this.state = 'active';
    this.#clearPersisted();
    this.#syncAll();
  }

  // ──────────────────────────────────────────────────────────────────
  // Rendering
  // ──────────────────────────────────────────────────────────────────

  #renderScaffold(): void {
    this.state = 'active';
    this.#renderProgress();
    this.#renderActions();
    // Land on the first eligible question.
    const first = this.questions.findIndex((q) => this.#isEligible(q));
    this.#activeIndex = first < 0 ? 0 : first;
  }

  #renderProgress(): void {
    const existing = this.$<HTMLElement>('[data-ry-target="progress"]');
    if (!this.hasAttribute('progress')) {
      existing?.remove();
      return;
    }
    if (existing) {
      this.#syncProgress(existing);
      return;
    }
    // Build detached so ry-step upgrades with its children already present.
    const rail = document.createElement('ry-step');
    rail.setAttribute('data-ry-target', 'progress');
    rail.className = 'ry-questionnaire__progress';
    this.#fillProgress(rail);
    this.insertBefore(rail, this.firstChild);
  }

  #fillProgress(rail: HTMLElement): void {
    rail.textContent = '';
    for (const q of this.eligible) {
      const step = document.createElement('span');
      step.textContent = q.getAttribute('prompt') ?? q.name;
      rail.appendChild(step);
    }
  }

  #syncProgress(rail: HTMLElement): void {
    const eligible = this.eligible;
    // Rebuild when the eligible set changed shape (a [when] branch flipped).
    if (rail.children.length !== eligible.length) this.#fillProgress(rail);
    const activeName = this.activeQuestion?.name ?? '';
    const activePos = eligible.findIndex((q) => q.name === activeName);
    [...rail.children].forEach((child, i) => {
      const step = child as HTMLElement;
      step.textContent = eligible[i]?.getAttribute('prompt') ?? eligible[i]?.name ?? '';
      if (i === activePos && !this.#complete) step.setAttribute('active', '');
      else step.removeAttribute('active');
      if (this.#complete || (activePos >= 0 && i < activePos)) step.setAttribute('completed', '');
      else step.removeAttribute('completed');
    });
  }

  #renderActions(): void {
    if (this.$('[data-ry-target="actions"]')) return;
    const bar = document.createElement('div');
    bar.setAttribute('data-ry-target', 'actions');
    bar.className = 'ry-questionnaire__actions';

    const prev = document.createElement('ry-button');
    prev.setAttribute('data-ry-target', 'prev');
    prev.setAttribute('variant', 'ghost');
    prev.textContent = this.getAttribute('prev-label') ?? 'Back';

    const skip = document.createElement('ry-button');
    skip.setAttribute('data-ry-target', 'skip');
    skip.setAttribute('variant', 'ghost');
    skip.textContent = this.getAttribute('skip-label') ?? 'Skip';

    const next = document.createElement('ry-button');
    next.setAttribute('data-ry-target', 'next');
    next.setAttribute('variant', 'primary');
    next.textContent = this.getAttribute('next-label') ?? 'Next';

    bar.append(prev, skip, next);
    this.appendChild(bar);
  }

  // ──────────────────────────────────────────────────────────────────
  // State sync
  // ──────────────────────────────────────────────────────────────────

  #syncAll(): void {
    const questions = this.questions;
    const activeName = this.activeQuestion?.name ?? '';

    for (const q of questions) {
      const eligible = q.name !== '' && this.#isEligible(q);
      q.setAttribute('data-ry-eligible', eligible ? 'true' : 'false');
      if (!eligible) {
        q.setAttribute('hidden', '');
        q.setAttribute('data-ry-state', 'pending');
        continue;
      }
      q.removeAttribute('hidden');
      if (this.#complete) {
        q.setAttribute('data-ry-state', 'done');
        q.setAttribute('hidden', '');
      } else if (q.name === activeName) {
        q.setAttribute('data-ry-state', 'active');
      } else {
        q.setAttribute('data-ry-state', q.answered ? 'done' : 'pending');
        q.setAttribute('hidden', '');
      }
    }

    const rail = this.$<HTMLElement>('[data-ry-target="progress"]');
    if (rail) this.#syncProgress(rail);
    this.#syncActions();
  }

  #syncActions(): void {
    const prev = this.$<HTMLElement>('[data-ry-target="prev"]');
    const skip = this.$<HTMLElement>('[data-ry-target="skip"]');
    const next = this.$<HTMLElement>('[data-ry-target="next"]');
    const active = this.activeQuestion;

    if (prev) {
      const atStart = this.#adjacentEligible(-1) === -1;
      if (atStart || this.#complete) prev.setAttribute('disabled', '');
      else prev.removeAttribute('disabled');
    }

    if (skip) {
      const showSkip =
        this.hasAttribute('skippable') &&
        !this.#complete &&
        active !== null &&
        !active.hasAttribute('required');
      if (showSkip) skip.removeAttribute('hidden');
      else skip.setAttribute('hidden', '');
    }

    if (next) {
      if (this.#complete) {
        next.setAttribute('hidden', '');
      } else {
        next.removeAttribute('hidden');
        const isLast = this.#adjacentEligible(1) === -1;
        next.textContent = isLast
          ? this.getAttribute('submit-label') ?? 'Submit'
          : this.getAttribute('next-label') ?? 'Next';
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Navigation internals
  // ──────────────────────────────────────────────────────────────────

  /**
   * Index of the nearest eligible question in `direction` from the active one,
   * or -1 when there isn't one (i.e. we're at an end of the flow).
   */
  #adjacentEligible(direction: 1 | -1): number {
    const questions = this.questions;
    for (let i = this.#activeIndex + direction; i >= 0 && i < questions.length; i += direction) {
      const q = questions[i];
      if (q && q.name !== '' && this.#isEligible(q)) return i;
    }
    return -1;
  }

  /** Activate `index`. Returns false when already there or when `ry:beforenavigate` was vetoed. */
  #goto(index: number): boolean {
    const from = this.#activeIndex;
    if (from === index) return false;
    const name = this.questions[index]?.name ?? '';
    if (!this.emit('beforenavigate', { from, to: index, name })) return false;
    this.#activeIndex = index;
    this.#complete = false;
    this.state = 'active';
    this.#syncAll();
    this.#persist();
    this.emit('navigate', { from, to: index, name });
    // shadcn parity: navigation moves focus to the newly active question.
    this.activeQuestion?.focusPrompt();
    return true;
  }

  #validate(q: RyQuestion): boolean {
    if (!q.hasAttribute('required') || q.answered) {
      q.setError(null);
      return true;
    }
    const message = `${q.getAttribute('prompt') || q.name} is required`;
    q.setError(message);
    this.emit('blocked', { name: q.name, message });
    q.focusPrompt();
    return false;
  }

  /**
   * Evaluate a question's [when] condition against the current answers.
   * Grammar: `name=a`, `name=a|b` (any of), `name!=a` (none of).
   */
  #isEligible(q: RyQuestion): boolean {
    const when = q.getAttribute('when');
    if (!when) return true;
    const negated = when.includes('!=');
    const [rawName, rawValues] = when.split(negated ? '!=' : '=');
    const name = (rawName ?? '').trim();
    if (!name) return true;
    const wanted = (rawValues ?? '').split('|').map((v) => v.trim()).filter(Boolean);

    const source = this.questions.find((other) => other.name === name);
    // Guard against a [when] that points at itself or at a question that is
    // itself ineligible — either way the branch can't be satisfied.
    const answer = source && source !== q && this.#isEligible(source) ? source.value : undefined;
    const have = answer === undefined ? [] : Array.isArray(answer) ? answer : [answer];
    const hit = wanted.some((w) => have.includes(w));
    return negated ? !hit : hit;
  }

  // ──────────────────────────────────────────────────────────────────
  // Events
  // ──────────────────────────────────────────────────────────────────

  #handleClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const action = target.closest('[data-ry-target]');
    if (!action || !this.contains(action)) return;
    switch (action.getAttribute('data-ry-target')) {
      case 'prev': this.prev(); break;
      case 'skip': this.skip(); break;
      case 'next': this.next(); break;
      default: return;
    }
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter') return;
    // Tag check rather than `instanceof HTMLTextAreaElement` — the constructor
    // isn't a global in every host the bundle runs in (the play runner's
    // happy-dom shim, for one), and referencing it there throws.
    if ((e.target as HTMLElement | null)?.tagName === 'TEXTAREA') return;
    // Stop here so a wrapping <ry-form> doesn't ALSO treat Enter as submit.
    e.preventDefault();
    e.stopPropagation();
    this.next();
  };

  #handleAnswerChange = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const question = target.closest('ry-question');
    if (!(question instanceof RyQuestion)) return;

    // A single click on a radio/checkbox surfaces here TWICE — once via the
    // native `input` event and once via the tile's `ry:change`. Dedupe on the
    // resulting value so one user action is one ry:answer.
    const snapshot = JSON.stringify([question.name, this.answers]);
    if (snapshot === this.#lastAnswerSnapshot) return;
    this.#lastAnswerSnapshot = snapshot;

    question.setError(null);
    // A changed answer can flip a downstream [when], so re-sync everything.
    this.#syncAll();
    this.#persist();
    this.emit('answer', {
      name: question.name,
      value: question.value,
      answers: this.answers,
    });
  };

  // ──────────────────────────────────────────────────────────────────
  // Persistence
  // ──────────────────────────────────────────────────────────────────

  #persistKey(): string | null {
    const key = this.getAttribute('persist');
    return key ? `${PERSIST_PREFIX}${key}` : null;
  }

  #persist(): void {
    const key = this.#persistKey();
    if (!key) return;
    const payload: PersistedRun = {
      answers: this.answers,
      activeName: this.activeQuestion?.name ?? '',
    };
    try { localStorage.setItem(key, JSON.stringify(payload)); } catch { /* quota / private mode */ }
  }

  #clearPersisted(): void {
    const key = this.#persistKey();
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }

  #restore(): void {
    const key = this.#persistKey();
    if (!key) return;
    let saved: PersistedRun | null = null;
    try {
      const raw = localStorage.getItem(key);
      saved = raw ? (JSON.parse(raw) as PersistedRun) : null;
    } catch { return; }
    if (!saved || typeof saved !== 'object' || !saved.answers) return;

    for (const q of this.questions) {
      const value = saved.answers[q.name];
      if (value === undefined) continue;
      const wanted = Array.isArray(value) ? value : [value];
      let matchedFree = false;
      for (const tile of q.tiles) {
        const input = tile.querySelector<HTMLInputElement>('input.ry-tile__input');
        if (!input) continue;
        const hit = wanted.includes(input.value);
        input.checked = hit;
        if (hit) tile.setAttribute('checked', '');
        else tile.removeAttribute('checked');
        if (hit) matchedFree = true;
      }
      // Anything that didn't match a choice is freeform text.
      const free = q.freeform;
      if (free) {
        const leftover = wanted.find(
          (v) => !q.tiles.some((t) => t.getAttribute('value') === v)
        );
        if (leftover !== undefined) free.value = leftover;
        else if (!matchedFree && wanted.length === 1) free.value = wanted[0] ?? '';
      }
    }

    // Restore position BY NAME so reordering questions doesn't corrupt the run.
    const idx = this.questions.findIndex((q) => q.name === saved?.activeName);
    if (idx >= 0) this.#activeIndex = idx;
  }
}

customElements.define('ry-choice', RyChoice);
customElements.define('ry-question', RyQuestion);
customElements.define('ry-questionnaire', RyQuestionnaire);
